/**
 * Exchange names used by the price service (prices.trigger.fun) and how the
 * app's own exchange codes map onto them.
 */

import type { ExchangeCode } from "@/lib/market-data/market-data.types";
import { ensureUsdtSymbol } from "@/utils/symbols";

export type FuturesServiceExchange =
  | "binance_futures"
  | "bybit_futures"
  | "gate_futures"
  | "kucoin_futures"
  | "hyperliquid"
  | "aster_futures";

export type SpotServiceExchange =
  | "binance_spot"
  | "bybit_spot"
  | "gate_spot"
  | "kucoin_spot";

export type ServiceExchange = FuturesServiceExchange | SpotServiceExchange;

export type ServiceMarketKind = "futures" | "spot";

/** One market that lists a token, as returned by `/v1/markets`. */
export interface ServiceMarket {
  exchange: ServiceExchange;
  /** `<BASE>USDT`, upper case. */
  symbol: string;
  market: ServiceMarketKind;
  /**
   * The service has a price for it now. A listed market without one (new
   * listing, quiet venue) still streams its price once the first tick comes.
   */
  priced: boolean;
}

/** Futures venues in the order the app prefers them for a live price. */
export const FUTURES_PRICE_PRIORITY: FuturesServiceExchange[] = [
  "binance_futures",
  "bybit_futures",
  "gate_futures",
  "kucoin_futures",
  "hyperliquid",
  "aster_futures",
];

/** Spot venues, used only when no futures venue lists the token. */
export const SPOT_PRICE_PRIORITY: SpotServiceExchange[] = [
  "binance_spot",
  "bybit_spot",
  "gate_spot",
  "kucoin_spot",
];

const FUTURES_BY_CODE: Record<ExchangeCode, FuturesServiceExchange> = {
  binance: "binance_futures",
  bybit: "bybit_futures",
  gate: "gate_futures",
  kucoin: "kucoin_futures",
  hyperliquid: "hyperliquid",
  asterdex: "aster_futures",
};

const SPOT_BY_CODE: Partial<Record<ExchangeCode, SpotServiceExchange>> = {
  binance: "binance_spot",
  bybit: "bybit_spot",
  gate: "gate_spot",
  kucoin: "kucoin_spot",
};

/**
 * Accepts every spelling the app uses for an exchange (`gate`, `gateio`,
 * `aster`, `asterdex`, `binance_futures`, display names, ...).
 */
export function toExchangeCode(
  value: string | null | undefined
): ExchangeCode | null {
  const lower = (value ?? "").toLowerCase();
  if (lower.includes("binance")) return "binance";
  if (lower.includes("bybit")) return "bybit";
  if (lower.includes("gate")) return "gate";
  if (lower.includes("kucoin")) return "kucoin";
  if (lower.includes("hyperliquid")) return "hyperliquid";
  if (lower.includes("aster")) return "asterdex";
  return null;
}

export function toFuturesServiceExchange(
  value: string | null | undefined
): FuturesServiceExchange | null {
  const code = toExchangeCode(value);
  return code ? FUTURES_BY_CODE[code] : null;
}

export function toSpotServiceExchange(
  value: string | null | undefined
): SpotServiceExchange | null {
  const code = toExchangeCode(value);
  return code ? SPOT_BY_CODE[code] ?? null : null;
}

export function isServiceExchange(value: string): value is ServiceExchange {
  return (
    (FUTURES_PRICE_PRIORITY as string[]).includes(value) ||
    (SPOT_PRICE_PRIORITY as string[]).includes(value)
  );
}

export function marketKindOf(exchange: ServiceExchange): ServiceMarketKind {
  return (SPOT_PRICE_PRIORITY as string[]).includes(exchange)
    ? "spot"
    : "futures";
}

/** `BTC`, `btc/usdt`, `BTCUSDT` → `BTCUSDT`. */
export function toServiceSymbol(symbol: string): string {
  return ensureUsdtSymbol(symbol);
}

/** `BTCUSDT` → `BTC`, the form `/v1/markets?symbol=` takes. */
export function toServiceBase(symbol: string): string {
  const full = toServiceSymbol(symbol);
  return full.endsWith("USDT") ? full.slice(0, -4) : full;
}

export function marketKey(exchange: ServiceExchange, symbol: string): string {
  return `${exchange}:${symbol}`;
}

/**
 * Picks the market the app shows a live price from.
 *
 * - Futures first: the requested exchange when it lists the token, else the
 *   first futures venue in FUTURES_PRICE_PRIORITY.
 * - Spot only when no futures venue lists the token (and `allowSpot`): the
 *   requested exchange's spot market first, then SPOT_PRICE_PRIORITY.
 * - Within each, a venue with a price wins over one without; a listed venue
 *   without a price is still used when it is the only one.
 */
export function chooseLivePriceMarket(
  markets: ServiceMarket[],
  options: { requested?: string | null; allowSpot: boolean }
): ServiceMarket | null {
  const pick = (order: Array<ServiceExchange | null>) => {
    for (const priced of [true, false]) {
      for (const exchange of order) {
        const market = exchange
          ? markets.find(
              (candidate) =>
                candidate.exchange === exchange && candidate.priced === priced
            )
          : undefined;
        if (market) return market;
      }
    }
    return null;
  };

  const futures = pick([
    toFuturesServiceExchange(options.requested),
    ...FUTURES_PRICE_PRIORITY,
  ]);
  if (futures || !options.allowSpot) return futures;

  return pick([toSpotServiceExchange(options.requested), ...SPOT_PRICE_PRIORITY]);
}
